/**
 * =====================================================================
 *  IMAGE CONFIG
 * =====================================================================
 *  All image references used across the site live here.
 *
 *  HOW TO REPLACE IMAGES:
 *  1. Local images: drop your file into `src/assets/` (subfolders below),
 *     then import it at the top of this file and use the imported
 *     variable as the value. Example:
 *
 *       import myLogo from "../assets/logo.png";
 *       ...
 *       logo: myLogo,
 *
 *  2. Remote/hosted images: just paste a full URL string instead, e.g.
 *       logo: "https://yourcdn.com/logo.png",
 *
 *  Member & event photos are matched by the `image` filename set in
 *  `siteContent.js` -> resolved here through `memberPhoto()` /
 *  `eventPhoto()` helper functions, OR you can simply hardcode a URL
 *  per person/event below in the `members` / `events` maps.
 * =====================================================================
 */

// --- Brand ---------------------------------------------------------
import logoImg from "../assets/apkanz-logo-v3.png";

import momentImage1 from "../assets/homepage-slider-1.jpg";
import momentImage2 from "../assets/homepage-slider-2.jpg";
import momentImage3 from "../assets/homepage-slider-3.jpg";
import momentImage4 from "../assets/homepage-slider-4.jpg";
import momentImage5 from "../assets/homepage-slider-5.jpg";


import president from "../assets/members/president-bujje-babu-nelluri.jpg";
// import vicePresident from "../assets/members/president-bujje-babu-nelluri.jpg";
// import generalSecretary from "../assets/members/";
import secretary from "../assets/members/guru-sai-sushwanth-konigeti-secretary.jpg";
import treasurer from "../assets/members/rajasimha-reddy-vutukur-treasurer.jpg";
import pramodEC from "../assets/members/pramod-eerugu-ec.jpg";
import lokeshEC from "../assets/members/lokesh-vemuri-ec.jpg";
import aravindaEC from "../assets/members/aravinda-singavarapu-ec.jpg";
import adiseshaiahEC from "../assets/members/adiseshaiah-nallapaneni-ec.jpg";
import karthikEC from "../assets/members/karthik-subramani-gendikotta-ec.jpeg"

export const brandImages = {
  logo: logoImg,
};

// --- Community moments carousel (shown below hero on homepage) ---------
const momentsCarouselImageMap = {
  "moment-1.jpg": momentImage1,
  "moment-2.jpg": momentImage2,
  "moment-3.jpg": momentImage3,
  "moment-4.jpg": momentImage4,
  "moment-5.jpg": momentImage5,
};

// --- Member photo map -------------------------------------------------
// Key = the `image` filename referenced in siteContent.js members list.
// Value = imported local asset OR a hosted URL. A neutral placeholder
// is used for any entry not listed here.
const memberPhotoMap = {
  "president.jpg": president,
  "vice-president.jpg": "https://placehold.co/300x300/E8541E/FFFFFF?text=Photo",
  "secretary.jpg": secretary,
  "general-secretary.jpg": "https://placehold.co/300x300/E8541E/FFFFFF?text=Photo",
  "treasurer.jpg": treasurer,
  "pramod-ec.jpg": pramodEC,
  "lokesh-ec.jpg": lokeshEC,
  "aravinda-ec.jpg": aravindaEC,
  "adiseshaiah-ec.jpg": adiseshaiahEC,
  "karthik-ec.jpg":  karthikEC,
  "Laxmanrao-ec.jpg": "https://placehold.co/300x300/E8541E/FFFFFF?text=Photo", 
};

export function memberPhoto(filename) {
  return memberPhotoMap[filename] || "https://placehold.co/300x300/CCCCCC/FFFFFF?text=Photo";
}

// --- Hero / homepage background ------------------------------------
export const heroImages = {
  background:
    "https://images.unsplash.com/photo-1634009413798-eab44d20603a?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  // Collage images shown on the homepage hero (Andhra heritage + NZ landscape)
  templeCollage:
    "https://images.unsplash.com/photo-1600842389784-43e6cfae50dd?q=80&w=1548&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  nzCollage:
    "https://images.unsplash.com/photo-1507699622108-4be3abd695ad?auto=format&fit=crop&w=800&q=80",
};

// --- Hero carousel slide images ---------------------------------------
// Key = the `image` filename referenced in siteContent.js home.heroSlides.
const heroSlideImageMap = {
  "slide-temple.jpg":
    "https://images.pexels.com/photos/15362127/pexels-photo-15362127.jpeg",
  "slide-festival.jpg":
    "https://images.unsplash.com/photo-1561539623-007d71660044?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  "slide-youth.jpg":
    "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1600&q=80",
  "slide-nz.jpg":
    "https://images.unsplash.com/photo-1507699622108-4be3abd695ad?auto=format&fit=crop&w=1600&q=80",
};

export function heroSlideImage(filename) {
  return heroSlideImageMap[filename] || "https://placehold.co/1600x800/1B2A5C/FFFFFF?text=APKANZ";
}

export function momentsCarouselImage(filename) {
  return momentsCarouselImageMap[filename] || "https://placehold.co/1200x700/CCCCCC/FFFFFF?text=APKANZ";
}

// --- Sponsor logos -------------------------------------------------------
// Key = the `logo` filename referenced in siteContent.js sponsors.list.
// Replace with real imported local assets or hosted URLs when available.
const sponsorLogoMap = {
  "sponsor-1.png": "https://placehold.co/220x110/00ff00/1B2A5C?text=Sponsor+One",
  "sponsor-2.png": "https://placehold.co/220x110/F2A93B/1B2A5C?text=Sponsor+Two",
  "sponsor-3.png": "https://placehold.co/220x110/E8541E/1B2A5C?text=Sponsor+Three",
  "sponsor-4.png": "https://placehold.co/220x110/1B285C/FFFFFF?text=Sponsor+Four",
  "sponsor-5.png": "https://placehold.co/220x110/2E8540/1B2A5C?text=Sponsor+Five",
  "sponsor-6.png": "https://placehold.co/220x110/1B285C/FFFFFF?text=Sponsor+Six",
};

export function sponsorLogo(filename) {
  return sponsorLogoMap[filename] || "https://placehold.co/220x110/CCCCCC/FFFFFF?text=Sponsor";
}

// --- State Symbols / Identity section images -------------------------
export const identityImages = {
  stateBird:
    "https://images.unsplash.com/photo-1628083371314-729e1291a595?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  stateAnimal:
    "https://images.unsplash.com/photo-1633183227780-f458624a8094?q=80&w=830&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  heritage:
    "https://images.pexels.com/photos/36771232/pexels-photo-36771232.jpeg",
};

// --- About page images -----------------------------------------------
export const aboutImages = {
  history:
    "https://images.pexels.com/photos/33811569/pexels-photo-33811569.jpeg",
};

// --- Event photo map ---------------------------------------------------
const eventPhotoMap = {
  "event-ugadi.jpg": "https://images.unsplash.com/photo-1604681630456-b5e7cd82e04d?auto=format&fit=crop&w=800&q=80",
  "event-sankranti.jpg": "https://images.unsplash.com/photo-1610375461369-d613b564f4c1?auto=format&fit=crop&w=800&q=80",
  "event-gala.jpg": "https://images.unsplash.com/photo-1599318011235-904154a09c36?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  "event-youth.jpg": "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=800&q=80",
};

export function eventPhoto(filename) {
  return eventPhotoMap[filename] || "https://placehold.co/800x500/CCCCCC/FFFFFF?text=Event";
}

// --- Gallery photo map ---------------------------------------------------
const galleryPhotoMap = {
  "gallery-1.jpg": "https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=700&q=80",
  "gallery-2.jpg": "https://images.unsplash.com/photo-1561539623-007d71660044?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  "gallery-3.jpg": "https://images.unsplash.com/photo-1601050690597-df0568f70950?auto=format&fit=crop&w=700&q=80",
  "gallery-4.jpg": "https://images.pexels.com/photos/15362127/pexels-photo-15362127.jpeg",
  "gallery-5.jpg": "https://images.unsplash.com/photo-1599318011235-904154a09c36?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
  "gallery-6.jpg": "https://images.unsplash.com/photo-1478147427282-58a87a120781?auto=format&fit=crop&w=700&q=80",
};

export function galleryPhoto(filename) {
  return galleryPhotoMap[filename] || "https://placehold.co/700x500/CCCCCC/FFFFFF?text=Gallery";
}
